<template>
  <div class="request-invoice">
    <NavHeader
      :title="$t('components.requestInvoice.title')"
      @onBackClick="goBack"
    />
    <div v-if="isLoading" class="py-6 d-flex justify-center align-center">
      <v-progress-circular indeterminate color="black" />
    </div>
    <template v-else>
      <template v-if="!showSuccessScreen">
        <div>
          <RequestInvoiceForm
            :loading="form.loading"
            :data-filled="dataFilled"
            :error="error"
            :preselected-data="preselectedCompanyData"
            :initial-receipt-data="receiptData"
            :display-vmi-fields="displayVmiFields"
            @submit="handleFormSubmit"
            @update:dataFilled="dataFilled = $event"
          />
        </div>
      </template>
      <template v-else>
        <LayoutCard rounded>
          <div
            class="d-flex flex-column justify-space-between"
            :style="{ minHeight: `75vh` }"
          >
            <div class="d-flex flex-column align-center justify-center">
              <img
                :src="require(`@/assets/images/icons/checkmark-success.svg`)"
                width="128"
              />
              <div class="tp-text-title-bold mt-8">
                {{ $t('components.requestInvoice.success.title') }}
              </div>
              <div class="tp-text-body text-center mt-3">
                {{ $t('components.requestInvoice.success.subtitle') }}
              </div>
            </div>
            <div class="px-6">
              <Button dark @click="goBack">
                <span>{{ $t('generic.back') }}</span>
              </Button>
            </div>
          </div>
        </LayoutCard>
      </template>
    </template>
  </div>
</template>

<script>
import NavHeader from '@/components/Navigation/NavHeader.vue';
import LayoutCard from '@/components/LayoutCard.vue';
import Button from '@/components/Button.vue';
import { parseQueryParam } from '@/utils/strings';
import RequestInvoiceForm from '@/components/Invoices/RequestInvoiceForm.vue';

export default {
  components: {
    NavHeader,
    LayoutCard,
    Button,
    RequestInvoiceForm,
  },
  data() {
    return {
      isLoading: true,
      receiptData: null,
      form: {
        loading: false,
      },
      showSuccessScreen: false,
      error: null,
      dataFilled: false,
      preselectedCompanyData: null,
      displayVmiFields: true,
    };
  },
  computed: {
    session() {
      return this.$route.query.session;
    },
    anonymousUserToken() {
      return this.$store.getters.anonymousUserToken;
    },
  },
  async mounted() {
    const lang = this.$route.query.language;
    const origin = this.$route.query.origin;
    const language = parseQueryParam(lang);

    if (this.session) {
      await this.$store.dispatch('fetchSessionData', { session: this.session });
    }

    if (origin === 'tip-success') {
      this.displayVmiFields = false;
    }

    this.$store.dispatch('sendAnalyticsEvent', {
      event: 'app_generate-invoice-vmi-receipt_pageview',
    });

    const paymentUuid = this.$route.params.paymentUuid;
    const existingData = await this.$store.dispatch('getInvoiceRequestData', {
      paymentUuid,
    });

    if (existingData) {
      if (
        existingData?.name ||
        existingData?.business_name ||
        existingData?.business_code ||
        existingData?.email
      ) {
        this.preselectedCompanyData = {
          business_name: existingData?.business_name,
          business_code: existingData?.business_code,
          business_address: existingData.business_address,
          business_vat_code: existingData?.business_vat_code,
          business_country_code: existingData?.business_country_code,
          name: existingData?.name,
          email: existingData?.email,
        };
        this.dataFilled = true;
      }
      if (existingData?.receipt_data) {
        this.receiptData = existingData.receipt_data;
      }
    } else {
      await this.getPreselectedCompanyData();
    }

    if (language) {
      this.$store.dispatch('changeLanguage', language);
    }
    this.$store.dispatch('sendAnalyticsEvent', {
      event: 'app_request-invoice_page-view',
    });

    this.isLoading = false;
  },
  methods: {
    handleFormSubmit({ receiptData, formData }) {
      this.submitData(formData, receiptData);
    },
    handleOldFlowSubmit(formData) {
      this.submitData(formData, null);
    },
    async submitData(formData, receiptData) {
      this.form.loading = true;
      this.error = null;
      const paymentUuid = this.$route.params.paymentUuid;

      const submitPayload = {
        payment_uuid: paymentUuid,
        name: formData.name,
        business_name: formData.businessName,
        business_code: formData.businessCode,
        business_address: formData.businessAddress,
        email: formData.email,
        business_vat_code: formData.enableVatCodeField
          ? formData.vatCode
          : null,
        business_country_code: formData.country,
        language: this.$i18n.locale(),
        anonymous_user_token: this.anonymousUserToken,
        save_request_invoice_details: formData.saveRequestInvoiceDetails,
      };

      // Only include receipt_data if using new flow
      if (receiptData) {
        submitPayload.receipt_data = receiptData;
      }

      try {
        await this.$store.dispatch('submitInvoiceRequest', {
          data: submitPayload,
        });
        this.showSuccessScreen = true;
      } catch (e) {
        if (e.response?.data?.message?.includes('Invoice cannot be changed')) {
          this.error = this.$i18n.translate(
            'components.requestInvoice.error.cannotUpdateInvoice'
          );
        } else if (
          e.response?.data?.message?.includes(
            'Invoice can be issued only after the order is closed'
          )
        ) {
          this.error = this.$i18n.translate(
            'components.requestInvoice.error.orderNotClosed'
          );
        } else if (
          e.response?.data?.message?.includes(
            'Invoice cannot be issued for this order'
          )
        ) {
          this.error = this.$i18n.translate(
            'components.requestInvoice.error.cannotIssueInvoice'
          );
        } else if (e.response?.data?.message?.includes('VAT Code invalid')) {
          this.error = this.$i18n.translate(
            'components.requestInvoice.error.vatCodeInvalid'
          );
        } else if (
          e.response?.data?.message?.includes(
            'email must be a valid email address'
          )
        ) {
          this.error = this.$i18n.translate(
            'components.requestInvoice.error.emailInvalid'
          );
        } else {
          this.error =
            e.response?.data?.message || this.$i18n.translate('generic.error');
        }
      }

      this.form.loading = false;
    },
    goBack() {
      this.$router.go(-1);
    },
    async getPreselectedCompanyData() {
      const anonymousUserToken = this.anonymousUserToken;
      try {
        const response = await this.axios.get(
          `/anonymous-user-legal-entity/${anonymousUserToken}`
        );
        const data = response.data;

        if (data) {
          this.preselectedCompanyData = {
            business_name: data.business_name,
            business_code: data.business_code,
            business_address: data.business_address,
            business_vat_code: data.business_vat_code,
            business_country_code: data.business_country_code,
            name: data.name,
            email: data.email,
          };
          this.dataFilled = true;
        }
      } catch (error) {
        console.error('Error fetching preselected company data:', error);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.request-invoice {
  display: flex;
  flex-flow: column;
  min-height: 100svh;
  height: 100%;
  padding-bottom: calc(env(safe-area-inset-bottom) + 40px);
}
</style>
